import classNames from "classnames";
import { FC, ReactNode, RefObject, useRef } from "react";

const NavItem: FC<{
  parentRef: RefObject<null>;
  children: ReactNode;
  onClick: () => void;
  className: string;
}> = ({ parentRef, children, onClick = () => {}, className }) => {
  const ref = useRef(null);

  return (
    <button
      type="button"
      ref={ref}
      onClick={() => {
        if (ref.current && parentRef.current) {
          const ref_ = ref.current as HTMLButtonElement;
          const parentRef_ = parentRef.current as HTMLDivElement;

          const relativeLeft =
            ref_.getBoundingClientRect().left -
            parentRef_.getBoundingClientRect().left +
            parentRef_.scrollLeft;

          parentRef_.scrollLeft = relativeLeft - 32;

          onClick();
        }
      }}
      className={className}
    >
      {children}
    </button>
  );
};

export const TabNav: FC<{
  navs: {
    title: string | ReactNode;
    to: string;
    onClick: (val: string) => void;
  }[];
  className?: string;
  tabClassName?: string;
  activeTab: string;
  variant: "nav" | "switcher";
  theme?: "plain";
}> = ({
  navs = [],
  className = "",
  tabClassName = "",
  activeTab,
  variant = "nav",
  theme = "plain",
}) => {
  const navRef = useRef(null);

  return (
    <div
      className={classNames(
        `hide-scroll-bar flex flex-row gap-3 overflow-x-scroll md:gap-4`,
        `!scroll-smooth transition-all duration-1000`,
        `bg-grey-50`,
        {
          "border-neut-200 rounded-[8px] bg-grey-50 p-1":
            variant === "switcher" && theme === "plain",
          "!gap-0": variant === "switcher",
        },
        className,
      )}
      ref={navRef}
    >
      {navs
        .filter((item) => item !== undefined)
        .map(({ title, to, onClick }, i) => {
          const key = `TabNav-${i}-${title}`;

          return (
            <NavItem
              parentRef={navRef}
              key={key}
              className={classNames(
                `relative flex min-w-max cursor-pointer items-center gap-1 text-center text-sm font-bold transition-all duration-300`,
                {
                  "flex-1": variant !== "nav",

                  "!text-primary": variant === "nav" && activeTab === to,

                  "p-1": variant === "nav",

                  "justify-center rounded-[4px] bg-transparent px-4 py-[6px] text-center text-grey-800":
                    variant === "switcher" && theme === "plain",

                  "bg-white !text-primary shadow-sm":
                    variant === "switcher" &&
                    theme === "plain" &&
                    activeTab === to,
                },
                `${tabClassName}`,
              )}
              onClick={() => {
                onClick(to);
              }}
            >
              {title}

              {variant === "nav" && (
                <div
                  className={`absolute bottom-0 left-0 right-full w-0 border-b-2 border-transparent transition-all duration-500 ${
                    activeTab === to &&
                    "!right-full !w-full rounded-full !border-primary"
                  }`}
                />
              )}
            </NavItem>
          );
        })}
    </div>
  );
};
